import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  endEnrichmentSession,
  endEnrichmentSessionBeacon,
  skipEnrichmentLead,
  startEnrichmentSession,
  type EnrichmentQueue,
  type MAWorkView,
  type EnrichmentSessionStartResponse,
} from "@/api/enrichment";

/**
 * Session lifecycle shared by the Work and Corrections queues.
 *
 * Owns the reservation, not the cursor: which row the operator is on stays
 * with each tab, because the two walk different payloads (companies vs
 * correction items).
 *
 * Releases are asymmetric on purpose, and this hook only performs the one
 * the backend can't do for us:
 *   - saving a lead (`enrichment_completed`) or closing a correction frees
 *     the row inside the same backend transaction — calling skip as well
 *     would release a row that is already gone;
 *   - an explicit skip is the only per-row release the frontend owns;
 *   - ending the session releases whatever is left, and the reservation
 *     lapsing is the backstop when the tab dies without telling us. That
 *     lapse is sized from the lot server-side, so only `expires_at` knows
 *     when it happens.
 *
 * Every one of those three — open, end, skip — moves rows in or out of the
 * shared queue, so each notifies `onQueueChanged` for the counter above the
 * Start button to re-read.
 */
export interface EnrichmentSessionState {
  session: EnrichmentSessionStartResponse | null;
  isOpening: boolean;
  /** Message from a failed open, already unwrapped from the response body.
   *  Two cases reach the operator here and both are written for them: a
   *  409 means the project has no "Wrong Owner" tag, a 400 means the lot
   *  exceeded the project's ceiling and names it. Show either as-is —
   *  restating the limit in our own words is how the front ends up
   *  carrying a second, staler copy of it. */
  openError: string | null;
  isEnding: boolean;
  /** Any integer >= 1. The ceiling belongs to the project, so an
   *  over-sized request goes out and comes back refused rather than being
   *  clamped here. */
  open: (count: number) => Promise<void>;
  end: () => Promise<void>;
  skip: (companyId: number) => Promise<void>;
}

/** Pull the server's own message out of an error body. The backend writes
 *  `detail` for a human, so we prefer it over anything we could phrase. */
function extractDetail(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return fallback;
}

/** Every mounted instance of this hook, so one can tell the others that
 *  the reservation is gone.
 *
 *  `/sessions/end` takes no queue: it releases everything the operator
 *  holds. With both queue tabs mounted at once an operator can hold a Work
 *  batch AND a Corrections batch, and ending either one frees both
 *  server-side. Without this, the other tab would keep rendering a batch
 *  the server no longer reserves — rows another operator may already be
 *  working. */
const mountedSessions = new Set<() => void>();

function broadcastSessionCleared() {
  for (const clear of mountedSessions) clear();
}

export function useEnrichmentSession(
  slug: string,
  queue: EnrichmentQueue,
  /** Called after any action that changes what the queue can hand out.
   *  Held in a ref so an inline arrow from the caller doesn't re-create
   *  `open` / `end` / `skip` on every render. */
  onQueueChanged?: () => void,
  workView: MAWorkView = "WORK",
): EnrichmentSessionState {
  const [session, setSession] = useState<EnrichmentSessionStartResponse | null>(
    null,
  );
  const [isOpening, setIsOpening] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  // Read by the teardown paths, which must not re-run when the session
  // object changes — only when the project does.
  const hasSessionRef = useRef(false);
  hasSessionRef.current = session !== null;

  const onQueueChangedRef = useRef(onQueueChanged);
  onQueueChangedRef.current = onQueueChanged;

  const open = useCallback(
    async (count: number) => {
      setIsOpening(true);
      setOpenError(null);
      try {
        const res = await startEnrichmentSession(slug, { queue, count, ...(queue === "work" ? { work_view: workView } : {}) });
        setSession(res);
        // The reserved rows just left the queue for everyone, this
        // operator included: the counter drops by the size of the lot.
        onQueueChangedRef.current?.();
      } catch (err) {
        setOpenError(
          extractDetail(err, "Couldn't open the session, try again."),
        );
      } finally {
        setIsOpening(false);
      }
    },
    [slug, queue, workView],
  );

  const end = useCallback(async () => {
    if (!hasSessionRef.current) return;
    setIsEnding(true);
    try {
      await endEnrichmentSession(slug);
    } catch (err) {
      // The rows expire on their own; failing to release now costs the
      // operator nothing and there is nothing useful to retry.
      console.warn("[Enrichment] session end failed", err);
    } finally {
      setIsEnding(false);
      // Clears this tab AND its sibling: the call released both.
      broadcastSessionCleared();
      // Whatever was left of both batches is back in the queues.
      onQueueChangedRef.current?.();
    }
  }, [slug]);

  // Register this instance's clearer for the duration of the mount.
  useEffect(() => {
    const clear = () => setSession(null);
    mountedSessions.add(clear);
    return () => {
      mountedSessions.delete(clear);
    };
  }, []);

  const skip = useCallback(
    async (companyId: number) => {
      try {
        await skipEnrichmentLead(slug, companyId);
        onQueueChangedRef.current?.();
      } catch (err) {
        // Non-blocking: the operator has already moved on, and the row is
        // released by the expiry at worst.
      console.warn("[Enrichment] skip failed", err);
      }
    },
    [slug],
  );

  // Tab close / hard navigation. `pagehide` fires in cases `beforeunload`
  // misses (bfcache, mobile Safari), and the beacon is idempotent, so
  // firing from both is harmless.
  useEffect(() => {
    const release = () => {
      if (hasSessionRef.current) endEnrichmentSessionBeacon(slug);
    };
    window.addEventListener("pagehide", release);
    window.addEventListener("beforeunload", release);
    return () => {
      window.removeEventListener("pagehide", release);
      window.removeEventListener("beforeunload", release);
    };
  }, [slug]);

  // NOTE: there is deliberately no release-on-unmount here. A session ends
  // two ways only — the operator presses End session, or the browser goes
  // away. Releasing when this component unmounts would take the batch off
  // an operator who merely looked at their stats and came back, which is
  // not them finishing. The Enrichment page keeps both queue tabs mounted
  // for the same reason, mirroring the Call Center. The reservation's own
  // expiry covers the case where the tab dies without either path firing.

  return {
    session,
    isOpening,
    openError,
    isEnding,
    open,
    end,
    skip,
  };
}
